import { GetStaticPaths, GetStaticProps } from 'next';
import { getAsyncProps } from '@builder.io/utils';

import { Page } from '../components/Page';
import { isProduction, zodParse } from '../utils';
import withRetries from '../utils/withRetries';
import {
  fetchAllPaths,
  fetchStaticPageContent,
  DataSets,
  PageContent,
  Z_PageContent,
} from '../builder';
import { fetchAllRoles, fetchAllCompanySizes } from '../rest/zapier';
import { fetchAsyncProps as HubSpotFormV2 } from '../components/HubSpotFormV2/HubSpotFormV2';
import {
  PrefetchedData,
  PrefetchedDataContext,
} from '../contexts/PrefetchedDataContext';
import { DATA_BINDING_REGEX } from '../components/RichTextHtml/RichTextHtml';

type Props = {
  content: PageContent;
  prefetchedData: PrefetchedData;
  urlPath?: string;
};

type Query = {
  slug?: string[];
};

export default function StaticPage(props: Props) {
  return (
    <PrefetchedDataContext.Provider value={props.prefetchedData}>
      <Page content={props.content} />
    </PrefetchedDataContext.Provider>
  );
}

export const getStaticProps: GetStaticProps<Props, Query> = async (context) => {
  const urlPath = `/${context.params?.slug?.join('/') || ''}`;

  const content: PageContent = await withRetries(() =>
    fetchStaticPageContent({ urlPath }),
  );

  if (!content) {
    return {
      // urlPath is used for client side data-fetching in when previewing/editing
      props: { urlPath },
      notFound: true,
      revalidate: 60,
    };
  }

  const dataSets = getDataSets(content);
  if (dataSets) {
    content.dataSets = dataSets;
  }

  await getAsyncProps(content, {
    HubSpotFormV2,
  });

  zodParse(Z_PageContent, content, 'PageContent');

  const baseURL = isProduction()
    ? 'https://zapier.com'
    : 'https://zapier-staging.com';

  const roles = await fetchAllRoles({ baseURL });
  const companySizes = await fetchAllCompanySizes({ baseURL });

  return {
    props: {
      content,
      prefetchedData: {
        roles,
        companySizes,
      },
    },
    revalidate: 60, // https://nextjs.org/docs/basic-features/data-fetching#incremental-static-regeneration
  };
};

export const getStaticPaths: GetStaticPaths<Query> = async () => {
  const paths = await fetchAllPaths();

  return {
    paths,
    fallback: true, // Enable Incremental Static Regeneration https://nextjs.org/docs/basic-features/data-fetching#fallback-true
  };
};

const getPropertiesByKey = (content: PageContent, propertyName: string) => {
  const matchingProperties: any[] = [];

  const findMatchingProperties = (element: any): void => {
    if (typeof element !== 'object') return;

    if (Array.isArray(element)) {
      element.forEach((el) => findMatchingProperties(el));
      return;
    }

    Object.entries(element || {}).forEach(([key, value]) => {
      if (key === propertyName) matchingProperties.push(value);
      findMatchingProperties(value);
    });
  };
  findMatchingProperties(content);
  return matchingProperties;
};

/**
 * Goes through content and finds all data-bindings that have been used within a block.
 * This is needed in order to only fetch data that is used in the data binding.
 */
export const getDataSets = (content: PageContent): DataSets | undefined => {
  const bindings: Record<string, string>[] = getPropertiesByKey(
    content,
    'bindings',
  );

  const bindingMatches = bindings.flatMap((binding) => {
    return Object.values(binding || {}).map((value) => {
      return value?.matchAll(
        /state\.(currentAccount|user|userProfile|queryParameters)\./g,
      );
    });
  });

  const dataSets = bindingMatches.reduce((acc, curr) => {
    const dataSet = Array.from(curr).flat()[1];
    acc.set(dataSet, true);
    return acc;
  }, new Map());

  /**
   * Go through all component options and find all values surrounded by the data binding regex
   * and add the first value in the dot notation to the dataSets.
   */
  getPropertiesByKey(content, 'options')
    .flatMap((options) => {
      return Object.values(options).filter(
        (option) => !!option && typeof option === 'string',
      );
    })
    .forEach((option) => {
      if (typeof option !== 'string') return;

      const matches = Array.from(option?.matchAll(DATA_BINDING_REGEX));
      if (matches.length)
        matches.forEach((match) => {
          const isEscaped = !!match?.[1];
          if (isEscaped) return;
          const key = match?.[3]?.split('.')?.[0]?.trim();
          dataSets.set(key, true);
        });
    });

  if (dataSets.size) return Object.fromEntries(dataSets);
};
